import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, animate, motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { useApp } from '../context/AppContext';
import { initials } from '../utils/format';

export function Logo({ light = false, to = '/' }) {
  return (
    <Link to={to} className={`logo ${light ? 'logo--light' : ''}`} aria-label="CTshaadi home">
      <motion.span className="logo__mark" whileHover={{ rotate: -8, scale: 1.08 }} transition={{ type: 'spring', stiffness: 300 }}>
        <svg viewBox="0 0 64 64" width="34" height="34" aria-hidden="true">
          <defs>
            <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#F0628E" />
              <stop offset="1" stopColor="#C2255C" />
            </linearGradient>
          </defs>
          <rect width="64" height="64" rx="18" fill="url(#lg)" />
          <path className="logo__heart" d="M32 49s-15-9.2-15-20.2C17 23 21.2 19 26 19c2.8 0 4.9 1.4 6 3.4 1.1-2 3.2-3.4 6-3.4 4.8 0 9 4 9 9.8C47 39.8 32 49 32 49z" fill="#fff" />
          <path className="logo__pulse" d="M24 31h5l2-4 3 8 2-4h4" fill="none" stroke="#D6336C" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.span>
      <span className="logo__word">
        CT<em>shaadi</em>
      </span>
    </Link>
  );
}

// Fades/slides children in when they scroll into view.
export function Reveal({ children, delay = 0, y = 28, className, as = 'div' }) {
  const M = motion[as];
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </M>
  );
}

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
export const rise = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

export function Page({ children, className = '' }) {
  return (
    <motion.main
      className={`page ${className}`}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.main>
  );
}

export function PageHead({ eyebrow, title, sub, children }) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
      </div>
      {children && <div className="page-head__actions">{children}</div>}
    </header>
  );
}

export function Counter({ to = 0, duration = 1.4, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    const c = animate(0, to, { duration, ease: 'easeOut', onUpdate: (x) => setV(Math.round(x)) });
    return () => c.stop();
  }, [inView, to, duration]);
  return (
    <span ref={ref}>
      {v.toLocaleString('en-IN')}
      {suffix}
    </span>
  );
}

export function Avatar({ src, name, size = 44, online, locked }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className="avatar" style={{ width: size, height: size }}>
      {src && !broken ? (
        <img src={src} alt={name} className={locked ? 'is-locked' : ''} onError={() => setBroken(true)} />
      ) : (
        <span className="avatar__fallback" style={{ fontSize: size * 0.36 }}>{initials(name)}</span>
      )}
      {online && <span className="avatar__dot" />}
    </span>
  );
}

export function Photo({ src, alt, locked, className = '' }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className={`photo ${className}`}>
      {src && !broken ? <img src={src} alt={alt} loading="lazy" className={locked ? 'is-locked' : ''} onError={() => setBroken(true)} /> : <div className="photo__empty"><Icon name="user" size={46} stroke={1.2} /></div>}
      {locked && (
        <div className="photo__lock">
          <Icon name="lock" size={18} /> Visible to Premium members
        </div>
      )}
    </div>
  );
}

export function MatchRing({ value = 0, size = 64, label = 'match' }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} className="ring__track" />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          className="ring__bar"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          whileInView={{ strokeDashoffset: c * (1 - value / 100) }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="ring__text">
        <strong>{value}%</strong>
        {size >= 60 && <small>{label}</small>}
      </div>
    </div>
  );
}

export function Tabs({ tabs, value, onChange, id = 'tabs' }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.value} role="tab" aria-selected={value === t.value} className={`tabs__item ${value === t.value ? 'is-active' : ''}`} onClick={() => onChange(t.value)}>
          {value === t.value && <motion.span layoutId={`${id}-pill`} className="tabs__pill" transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
          <span className="tabs__label">
            {t.icon && <Icon name={t.icon} size={16} />}
            {t.label}
            {!!t.count && <span className="tabs__count">{t.count}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

export function Chip({ active, children, onClick, icon }) {
  return (
    <motion.button type="button" whileTap={{ scale: 0.94 }} className={`chip ${active ? 'is-active' : ''}`} onClick={onClick}>
      {active ? <Icon name="check" size={14} stroke={2.4} /> : icon && <Icon name={icon} size={14} />}
      {children}
    </motion.button>
  );
}

export function ChipSelect({ options = [], value = [], onChange, anyLabel = 'Any' }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="chips">
      {anyLabel && <Chip active={!value.length} onClick={() => onChange([])}>{anyLabel}</Chip>}
      {options.map((o) => (
        <Chip key={o} active={value.includes(o)} onClick={() => toggle(o)}>
          {o}
        </Chip>
      ))}
    </div>
  );
}

export function Field({ label, hint, badge, children, className = '' }) {
  return (
    <label className={`field ${className}`}>
      {label && (
        <span className="field__label">
          {label}
          {badge && <span className="field__badge">{badge}</span>}
        </span>
      )}
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Select({ value, onChange, options, placeholder = 'Select', ...rest }) {
  return (
    <select className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...rest}>
      <option value="">{placeholder}</option>
      {options.map((o) => (typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export function Modal({ open, onClose, children, width = 520, title }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose}>
          <motion.div
            className="modal__card"
            style={{ maxWidth: width }}
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {title && (
              <div className="modal__head">
                <h3>{title}</h3>
                <button className="icon-btn" onClick={onClose} aria-label="Close">
                  <Icon name="x" />
                </button>
              </div>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function EmptyState({ icon = 'heart', title, text, action }) {
  return (
    <motion.div className="empty" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
      <motion.div className="empty__icon" animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
        <Icon name={icon} size={34} />
      </motion.div>
      <h3>{title}</h3>
      {text && <p className="muted">{text}</p>}
      {action}
    </motion.div>
  );
}

export function Loader({ label = 'Finding beautiful matches…' }) {
  return (
    <div className="loader">
      <motion.div className="loader__heart" animate={{ scale: [1, 1.18, 1, 1.12, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
        <Icon name="heart" size={34} fill="currentColor" stroke={0} />
      </motion.div>
      <span className="muted">{label}</span>
    </div>
  );
}

export function SkeletonGrid({ count = 6 }) {
  return (
    <div className="grid-cards">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton skeleton--photo" />
          <div className="skeleton skeleton--line" />
          <div className="skeleton skeleton--line short" />
        </div>
      ))}
    </div>
  );
}

// Soft floating hearts/petals for hero backgrounds.
export function Petals({ count = 14 }) {
  const items = useRef(
    Array.from({ length: count }, (_, i) => ({
      left: (i * 73) % 100,
      size: 10 + ((i * 37) % 18),
      delay: (i * 1.3) % 9,
      duration: 11 + ((i * 7) % 9),
      heart: i % 3 !== 0,
    })),
  ).current;
  return (
    <div className="petals" aria-hidden="true">
      {items.map((p, i) => (
        <span
          key={i}
          className={`petal ${p.heart ? 'petal--heart' : ''}`}
          style={{ left: `${p.left}%`, width: p.size, height: p.size, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }}
        />
      ))}
    </div>
  );
}

export function Toasts() {
  const { toasts } = useApp();
  return (
    <div className="toasts" aria-live="polite">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            layout
            className={`toast toast--${t.tone}`}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            <Icon name={t.tone === 'error' ? 'x' : t.tone === 'success' ? 'check' : 'sparkle'} size={16} stroke={2.2} />
            {t.text}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

export function Confetti({ run }) {
  const pieces = useRef(Array.from({ length: 40 }, (_, i) => ({ x: (Math.random() - 0.5) * 600, r: Math.random() * 720, d: Math.random() * 0.3, c: ['#D6336C', '#E8A623', '#0FA3B1', '#7C4DFF', '#F0628E'][i % 5] }))).current;
  return (
    <AnimatePresence>
      {run && (
        <div className="confetti" aria-hidden="true">
          {pieces.map((p, i) => (
            <motion.span
              key={i}
              style={{ background: p.c }}
              initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
              animate={{ x: p.x, y: [0, -220 - Math.random() * 120, 420], opacity: [1, 1, 0], rotate: p.r }}
              transition={{ duration: 1.8, delay: p.d, ease: 'easeOut' }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}
